import * as signalR from '@microsoft/signalr';
import { DeviceStatusResponse, IUploadEstimates } from '@trackman/baseball-shared-components';
import { ISessionMode } from 'interfaces/session/ISessionMode';
import { IApiSessionMetadataUpdated } from 'interfaces/tagging/ApiModels/IApiSessionMetadataUpdated';
import { ISaltUpdate, ISoftwareUpdate } from 'interfaces/tagging/ApiModels/IUpdateMessage';
import { injectable } from 'inversify';
import { SessionStore } from 'stores/SessionStore';
import { TrackingElementsStore } from 'stores/TrackingElementsStore';
import { IFieldInfo } from '../../interfaces/IFieldInfo';
import { IPitchBounceValidationDecision } from '../../interfaces/tagging/ApiModels/IPitchBounceValidationDecision';
import { IStrikeZoneDecisionModel } from '../../interfaces/tagging/ApiModels/IStrikeZoneDecision';
import VideoUploaderStateStore from '../../stores/VideoUploaderStateStore';
import { LogService } from './LogService';

const RETRY_INTERVAL_TIME = 3000;
@injectable()
export class BroadcastConsumerService {
  private connection: signalR.HubConnection;

  private broadcastUrl: string;

  private logService: LogService;

  private videoUploaderStateStore: VideoUploaderStateStore;

  private onBallFinalReceived?: (ballFinal: any) => void;

  private onPitchLiveReceived?: (pitchLive: any) => void;

  private onSpin3DFinalReceived?: (spin3dFinal: any) => void;

  private onHitLiveReceived?: (hitLive: any) => void;

  private onActiveClientsChangedReceived?: (activeClientChanged: any) => void;

  private onSessionModeChangedReceived?: (mode: ISessionMode) => void;

  private onSessionMetadataUpdatedReceived?: (metadata: IApiSessionMetadataUpdated) => void;

  private onActiveFieldChangedReceived?: (newField?: IFieldInfo) => void;

  private onLoggedInChangedReceived?: (userData?: any) => void;

  private onStrikeZoneDecisionReceived?: (strikeZoneDecision: IStrikeZoneDecisionModel) => void;

  private onPitchBounceValidationDecisionReceived?: (pitchBounceValidationDecision: IPitchBounceValidationDecision) => void;

  private onSaltAndServicesDeploymentStatusChanged?: (saltUpdate: ISaltUpdate) => void;

  private onPtuDeploymentStatusChanged?: (softwareUpdate: ISoftwareUpdate) => void;

  private onDeviceStatusChangedReceived?: (deviceStatusResponse: DeviceStatusResponse) => void;

  private sessionStore: SessionStore;

  private onCloseCount = 0;

  private errorCount = 0;

  private trackingElementsStore: TrackingElementsStore;

  constructor(
    broadcastUrl: string,
    sessionStore: SessionStore,
    logService: LogService,
    videoUploaderStateStore: VideoUploaderStateStore,
    trackingElementsStore: TrackingElementsStore
  ) {
    this.broadcastUrl = broadcastUrl;
    this.sessionStore = sessionStore;
    this.logService = logService;
    this.videoUploaderStateStore = videoUploaderStateStore;
    this.trackingElementsStore = trackingElementsStore;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(this.broadcastUrl)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          this.errorCount = retryContext.previousRetryCount + 1;
          return RETRY_INTERVAL_TIME;
        },
      } as signalR.IRetryPolicy)
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.connection.onreconnected(() => {
      logService.logError({
        error: 'Reconnected',
        message: `Connection to TaggingApp reconnected after ${this.errorCount} attempts`,
      });
    });

    this.connection.onreconnecting((error: Error | undefined) => {
      console.warn('Connection to TaggingApp signalR is closed. Reconnecting...');
    });
  }

  public onUploaderEstimatesChanged = (): void => {
    this.connection.on('UploaderEstimatesChanged', (estimates: IUploadEstimates) => {
      this.videoUploaderStateStore.setCompositeImages(estimates.compositeImages);
      this.videoUploaderStateStore.setMeasurementsAndMetadata(estimates.measurementsAndMetadata);
      this.videoUploaderStateStore.setPlayerPositioningData(estimates.playerPositioning);
      this.videoUploaderStateStore.setPlayVideo(estimates.playVideo);
      this.videoUploaderStateStore.setMainComponentUploadStatus(estimates.uploadStatus);
      this.videoUploaderStateStore.setBatTrackingVideo(estimates.batTrackingVideo);
      this.videoUploaderStateStore.setTime(estimates.time);
      this.videoUploaderStateStore.setIsInternetConnectionAvailable(estimates.isInternetConnectionAvailable);

      this.videoUploaderStateStore.setTotalTimeRemaining(
        [
          estimates.compositeImages,
          estimates.measurementsAndMetadata,
          estimates.playerPositioning,
          estimates.playVideo,
          estimates.batTrackingVideo,
        ].map((e) => e?.estimatedTimeMiliseconds || 0)
      );
    });
  };

  public subscribeBallEvents = (onBallFinalReceived: (ballFinal: any) => void, onPitchLiveReceived: (pitchLive: any) => void) => {
    this.onBallFinalReceived = onBallFinalReceived;
    this.onPitchLiveReceived = onPitchLiveReceived;
    this.ballEventsSubscribe();
  };

  public subscribeStrikeZoneDecision = (onStrikeZoneDecision: (strikeZoneDecision: IStrikeZoneDecisionModel) => void) => {
    this.onStrikeZoneDecisionReceived = onStrikeZoneDecision;
    if (this.onStrikeZoneDecisionReceived) {
      this.connection.on('ReceiveStrikeZoneDecision', this.onStrikeZoneDecisionReceived);
    }
  };

  public subscribeBallFinal = (onBallFinalReceived: (ballFinal: any) => void) => {
    this.onBallFinalReceived = onBallFinalReceived;
    this.ballEventsSubscribe();
  };

  public subscribeSpin3DFinal = (onSpin3DFinalReceived: (spin3DFinal: any) => void) => {
    this.onSpin3DFinalReceived = onSpin3DFinalReceived;
    this.spin3DFinalEventSubscribe();
  };

  public subscribeHitLive = (onHitLiveReceived: (hitLive: any) => void) => {
    this.onHitLiveReceived = onHitLiveReceived;
    this.hitLiveEventSubscribe();
  };

  public subscribeSaltAndServicesDeploymentStatusChanged = (
    onSaltAndServicesDeploymentStatusChanged: (saltUpdate: ISaltUpdate) => void
  ) => {
    this.onSaltAndServicesDeploymentStatusChanged = onSaltAndServicesDeploymentStatusChanged;
    if (this.onSaltAndServicesDeploymentStatusChanged) {
      this.connection.on('SaltAndServicesDeploymentStatusChanged', this.onSaltAndServicesDeploymentStatusChanged);
    }
  };

  public subscribePtuDeploymentStatusChanged = (onPtuDeploymentStatusChanged: (softwareUpdate: ISoftwareUpdate) => void) => {
    this.onPtuDeploymentStatusChanged = onPtuDeploymentStatusChanged;
    if (this.onPtuDeploymentStatusChanged) {
      this.connection.on('ReceivePtuDeploymentStatusChanged', this.onPtuDeploymentStatusChanged);
    }
  };

  public subscribeActiveClientsChanged = (onActiveClientsChangedReceived: (activeClientsChanged: any) => void) => {
    this.onActiveClientsChangedReceived = onActiveClientsChangedReceived;
    this.activeClientsChangedSubscribe();
  };

  public subscribeSessionDataChanged = (
    onSessionModeChangedReceived: (sessionModeChanged: ISessionMode) => void,
    onSessionMetadataUpdatedReceived: (metadataUpdated: IApiSessionMetadataUpdated) => void
  ) => {
    this.onSessionModeChangedReceived = onSessionModeChangedReceived;
    this.onSessionMetadataUpdatedReceived = onSessionMetadataUpdatedReceived;
    this.sessionDataChangedSubscribe();
  };

  public subscribeActiveFieldChanged = (activeFieldChangedReceived: (newField?: IFieldInfo) => void) => {
    this.onActiveFieldChangedReceived = activeFieldChangedReceived;
    this.activeFieldChangedSubscribe();
  };

  public subscribeLoggedInChanged = (loggedInChangedReceived: (userData?: any) => void) => {
    this.onLoggedInChangedReceived = loggedInChangedReceived;
    this.loggedInChangedSubscribe();
  };

  public subscribePitchBounceValidationDecision = (
    onPitchBounceValidationDecisionReceived: (pitchCalibrationDecision: IPitchBounceValidationDecision) => void
  ) => {
    this.onPitchBounceValidationDecisionReceived = onPitchBounceValidationDecisionReceived;
    if (this.onPitchBounceValidationDecisionReceived) {
      this.connection.on('ReceivePitchBounceValidationDecision', this.onPitchBounceValidationDecisionReceived);
    }
  };

  public unsubscribePitchBounceValidationDecision = () => {
    if (this.onPitchBounceValidationDecisionReceived) {
      this.connection.off('ReceivePitchBounceValidationDecision', this.onPitchBounceValidationDecisionReceived);
      this.onPitchBounceValidationDecisionReceived = undefined;
    }
  };

  private sessionDataChangedSubscribe = () => {
    if (this.onSessionModeChangedReceived) {
      this.connection.on('ReceiveSessionModeChanged', this.onSessionModeChangedReceived);
    }
    if (this.onSessionMetadataUpdatedReceived) {
      this.connection.on('ReceiveSessionMetadataUpdated', this.onSessionMetadataUpdatedReceived);
    }
  };

  private activeFieldChangedSubscribe = () => {
    if (this.onActiveFieldChangedReceived) {
      this.connection.on('ReceiveActiveField', this.onActiveFieldChangedReceived);
    }
  };

  private loggedInChangedSubscribe = () => {
    if (this.onLoggedInChangedReceived) {
      this.connection.on('ReceiveLoggedInUser', this.onLoggedInChangedReceived);
    }
  };

  private ballEventsSubscribe = () => {
    if (this.onBallFinalReceived) {
      this.connection.on('ReceiveBallFinal', this.onBallFinalReceived);
    }
    if (this.onPitchLiveReceived) {
      this.connection.on('ReceivePitchLive', this.onPitchLiveReceived);
    }
  };

  private spin3DFinalEventSubscribe = () => {
    if (this.onSpin3DFinalReceived) {
      this.connection.on('ReceiveSpin3DFinal', this.onSpin3DFinalReceived);
    }
  };

  private hitLiveEventSubscribe = () => {
    if (this.onHitLiveReceived) {
      this.connection.on('ReceiveHitLive', this.onHitLiveReceived);
    }
  };

  private activeClientsChangedSubscribe = () => {
    if (this.onActiveClientsChangedReceived) {
      this.connection.on('ActiveClientsChanged', this.onActiveClientsChangedReceived);
    }
  };

  public unsubscribeBallEvents = () => {
    this.onBallFinalReceived = undefined;
    this.onPitchLiveReceived = undefined;
    this.ballEventsUnsubscribe();
  };

  public unsubscribeSpin3DFinalEvent = () => {
    this.onSpin3DFinalReceived = undefined;
    this.logService.logInfo(`Unsubscribing from Spin3DFinal event`);
    this.connection.off('ReceiveSpin3DFinal');
  };

  public unsubscribeSessionDataChanged = () => {
    this.onSessionModeChangedReceived = undefined;
    this.onSessionMetadataUpdatedReceived = undefined;
    this.sessionDataChangedUnsubscribe();
  };

  public unsubscribeStrikeZoneDecision = () => {
    if (this.onStrikeZoneDecisionReceived) {
      this.connection.off('ReceiveStrikeZoneDecision');
    }
    this.onStrikeZoneDecisionReceived = undefined;
  };

  public unsubscribeSaltAndServicesDeploymentStatusChanged = () => {
    if (this.onSaltAndServicesDeploymentStatusChanged) {
      this.connection.off('SaltAndServicesDeploymentStatusChanged');
    }
    this.onSaltAndServicesDeploymentStatusChanged = undefined;
  };

  public unsubscribePtuDeploymentStatusChanged = () => {
    if (this.onPtuDeploymentStatusChanged) {
      this.connection.off('PtuDeploymentStatusChanged');
    }
    this.onPtuDeploymentStatusChanged = undefined;
  };

  public subscribeOnDeviceStatusChanged = (callback: (deviceStatusResponse: DeviceStatusResponse) => void) => {
    this.onDeviceStatusChangedReceived = callback;
    if (this.onDeviceStatusChangedReceived) {
      this.connection.on('DeviceStatusChanged', this.onDeviceStatusChangedReceived);
    }
  };

  private ballEventsUnsubscribe = () => {
    this.logService.logInfo(`Unsubscribing from ball events`);
    this.connection.off('ReceiveBallFinal');
    this.connection.off('ReceivePitchLive');
  };

  private sessionDataChangedUnsubscribe = () => {
    this.logService.logInfo(`Unsubscribing from session events`);
    this.connection.off('ReceiveSessionModeChanged');
    this.connection.off('ReceiveSessionMetadataUpdated');
  };

  private unsubscribeActiveFieldChanged = () => {
    this.onActiveFieldChangedReceived = undefined;
    this.connection.off('ReceiveSystemConfiguration');
  };

  private onDeviceStatusChanged = () => {
    this.connection.on('DeviceStatusChanged', (deviceStatusResponse: DeviceStatusResponse) => {
      this.trackingElementsStore.setStatuses(deviceStatusResponse.statuses);
    });
  };

  start = async () => {
    try {
      this.ballEventsSubscribe();
      this.sessionDataChangedSubscribe();
      this.subscribeSpin3DFinal(() => {});
      this.subscribeHitLive(() => {});
      this.subscribeActiveClientsChanged(() => {});
      this.onDeviceStatusChanged();

      await this.connection.start().then(() => {
        this.logService.logInfo(`Succesfully connected to SignalR`);
      });
    } catch (err) {
      console.warn('Exception when connecting to TaggingApp SignalR. Retrying...');
      setTimeout(() => this.start(), 5000);
    }
    this.setDatabroadcasterConnectivity();
  };

  setDatabroadcasterConnectivity = () => {
    switch (this.connection.state) {
      case signalR.HubConnectionState.Connected:
        this.sessionStore.setIsDataBroadcasterConnected(true);
        break;
      case signalR.HubConnectionState.Disconnected:
        this.sessionStore.setIsDataBroadcasterConnected(false);
        break;
      default:
        this.sessionStore.setIsDataBroadcasterConnected(false);
    }
  };
}
